import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'src/components/input'
import { Button } from 'src/components/button'
import { googlePlacesApiKey, isGooglePlacesConfigured } from 'src/config/google-places'

/**
 * A generic structured address shape — survey-agnostic.
 * All fields are optional so consumers can decide what to persist.
 */
export type StructuredAddress = {
  streetAddress?: string
  city?: string
  state?: string
  postalCode?: string
  country?: string
  latitude?: number | null
  longitude?: number | null
  placeId?: string | null
  rawAddress?: string
}

/** Parse a Google Places result into a StructuredAddress. */
export function parsePlaceResult(place: any): StructuredAddress {
  const components = place.addressComponents ?? []
  const get = (type: string, nameType: 'longText' | 'shortText' = 'longText') =>
    components.find((c: any) => c.types.includes(type))?.[nameType] ?? ''

  const streetNumber = get('street_number')
  const route = get('route')
  const rawAddress = place.formattedAddress ?? ''
  // Follow the country's order as Google formats it: "Calle 44A # 70-71" in
  // Colombia, "123 Main St" in the US.
  const routeFirst =
    Boolean(route && streetNumber) &&
    rawAddress.includes(route) &&
    (!rawAddress.includes(streetNumber) ||
      rawAddress.indexOf(route) < rawAddress.indexOf(streetNumber))
  const streetAddress = (routeFirst ? [route, streetNumber] : [streetNumber, route])
    .filter(Boolean)
    .join(' ')
  const city = get('locality') || get('sublocality')
  const state = get('administrative_area_level_1', 'shortText')
  const postalCode = get('postal_code')
  const country = get('country', 'shortText')
  const rawLat = place.location?.lat() ?? null
  const rawLng = place.location?.lng() ?? null
  const latitude = rawLat !== null ? Math.round(rawLat * 1e6) / 1e6 : null
  const longitude = rawLng !== null ? Math.round(rawLng * 1e6) / 1e6 : null
  const placeId = place.id ?? null

  return {
    rawAddress,
    streetAddress,
    city,
    state,
    postalCode: postalCode || undefined,
    country,
    latitude,
    longitude,
    placeId,
  }
}

// --- Google Maps script loader ---

let loadPromise: Promise<void> | null = null

function loadGoogleMapsScript(): Promise<void> {
  if (loadPromise) return loadPromise

  const g = (window as any).google
  if (typeof g !== 'undefined' && g.maps?.places) {
    return Promise.resolve()
  }
  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googlePlacesApiKey}&libraries=places&v=weekly`
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      loadPromise = null
      reject(new Error('Failed to load Google Maps script'))
    }
    document.head.appendChild(script)
  })
  return loadPromise
}

// --- Google Places autocomplete mode ---

function GoogleAutocompleteInput({
  onChange,
  value,
  disabled,
}: {
  onChange: (value: StructuredAddress | null) => void
  value: StructuredAddress | null
  disabled: boolean
}) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const elementRef = useRef<any>(null)
  const [loading, setLoading] = useState(true)
  const [fetching, setFetching] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const handleSelect = useCallback(
    async (e: any) => {
      const placePrediction = e.placePrediction
      if (!placePrediction) return
      setFetching(true)
      try {
        const place = placePrediction.toPlace()
        await place.fetchFields({
          fields: ['addressComponents', 'formattedAddress', 'location'],
        })
        onChange(parsePlaceResult(place))
      } finally {
        setFetching(false)
      }
    },
    [onChange],
  )

  useEffect(() => {
    if (value) return

    let cancelled = false
    setLoading(true)

    async function init() {
      try {
        await loadGoogleMapsScript()
        if (cancelled || !containerRef.current) return

        const g = (window as any).google
        const el = new g.maps.places.PlaceAutocompleteElement({})
        el.setAttribute('style', 'width: 100%; color-scheme: light; background: transparent;')
        elementRef.current = el
        containerRef.current.appendChild(el)
        el.addEventListener('gmp-select', handleSelect)
      } catch {
        if (!cancelled) setLoadError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    init()

    return () => {
      cancelled = true
      if (elementRef.current) {
        elementRef.current.removeEventListener('gmp-select', handleSelect)
        elementRef.current.remove()
        elementRef.current = null
      }
    }
  }, [value, handleSelect])

  if (value) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 rounded-md border bg-surface p-2 text-text-primary">
            {value.rawAddress}
          </div>
          <Button
            variant="ghost"
            tone="neutral"
            iconOnly
            shape="circle"
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            className="transition-colors"
            aria-label={t('addressField.clearAddress')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm text-text-placeholder">
          {value.streetAddress && (
            <div className="col-span-2">
              <span className="font-medium">{t('addressField.streetPrefix')} </span>
              {value.streetAddress}
            </div>
          )}
          {value.city && (
            <div>
              <span className="font-medium">{t('addressField.cityPrefix')} </span>
              {value.city}
            </div>
          )}
          {value.state && (
            <div>
              <span className="font-medium">{t('addressField.statePrefix')} </span>
              {value.state}
            </div>
          )}
          {value.postalCode && (
            <div>
              <span className="font-medium">{t('addressField.postalPrefix')} </span>
              {value.postalCode}
            </div>
          )}
          {value.country && (
            <div>
              <span className="font-medium">{t('addressField.countryPrefix')} </span>
              {value.country}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loadError) {
    return <div className="text-sm text-error">{t('addressField.loadError')}</div>
  }

  return (
    <div className="relative w-full">
      {loading && (
        <div className="flex items-center gap-2 rounded-md border bg-surface p-2 text-sm text-text-tertiary">
          {t('addressField.loadingSearch')}
        </div>
      )}
      <div ref={containerRef} className={loading ? 'hidden' : 'gmp-autocomplete-wrapper w-full'} />
      {fetching && (
        <div className="mt-1 flex items-center gap-2 text-sm text-text-tertiary">
          {t('addressField.fetchingDetails')}
        </div>
      )}
    </div>
  )
}

// --- Manual fallback mode ---

type ManualFormState = {
  streetAddress: string
  city: string
  state: string
  postalCode: string
  country: string
}

function ManualAddressInput({
  onChange,
  value,
  disabled,
}: {
  onChange: (value: StructuredAddress | null) => void
  value?: StructuredAddress | null
  disabled: boolean
}) {
  const { t } = useTranslation()
  const [fields, setFields] = useState<ManualFormState>({
    streetAddress: value?.streetAddress ?? '',
    city: value?.city ?? '',
    state: value?.state ?? '',
    postalCode: value?.postalCode ?? '',
    country: value?.country ?? '',
  })

  const emitChange = (updated: ManualFormState) => {
    const { streetAddress, city, state, postalCode, country } = updated
    const rawAddress = [streetAddress, city, state, postalCode, country].filter(Boolean).join(', ')
    onChange({
      rawAddress,
      streetAddress,
      city,
      state,
      postalCode,
      country,
      latitude: null,
      longitude: null,
      placeId: null,
    })
  }

  const handleChange =
    (field: keyof ManualFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const updated = { ...fields, [field]: e.target.value }
      setFields(updated)
      emitChange(updated)
    }

  return (
    <div className="flex flex-col gap-3">
      <Input
        label={t('addressField.streetLabel')}
        placeholder={t('addressField.streetPlaceholder')}
        value={fields.streetAddress}
        onChange={handleChange('streetAddress')}
        disabled={disabled}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t('addressField.cityLabel')}
          placeholder={t('addressField.cityLabel')}
          value={fields.city}
          onChange={handleChange('city')}
          disabled={disabled}
        />
        <Input
          label={t('addressField.stateLabel')}
          placeholder={t('addressField.stateLabel')}
          value={fields.state}
          onChange={handleChange('state')}
          disabled={disabled}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={t('addressField.postalCodeLabel')}
          placeholder={t('addressField.postalCodeLabel')}
          value={fields.postalCode}
          onChange={handleChange('postalCode')}
          disabled={disabled}
        />
        <Input
          label={t('addressField.countryLabel')}
          placeholder={t('addressField.countryLabel')}
          value={fields.country}
          onChange={handleChange('country')}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

// --- Main component ---

interface AddressAutocompleteFieldProps {
  value?: StructuredAddress | null
  onChange: (value: StructuredAddress | null) => void
  disabled?: boolean
}

export const AddressAutocompleteField: FC<AddressAutocompleteFieldProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  if (isGooglePlacesConfigured) {
    return <GoogleAutocompleteInput onChange={onChange} value={value ?? null} disabled={disabled} />
  }

  return <ManualAddressInput onChange={onChange} value={value} disabled={disabled} />
}
